/**
 * Zustand store with persistence and offline-first sync.
 *
 * Architecture:
 * - Slices: auth, plans, config, ui, sync
 * - Persist middleware: localStorage backup
 * - Future: sync middleware to push to Supabase when online
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { PlanRates, UserConfig, ConfigId } from '@/types'
import { BASE_MATRIX_DATA } from '@/data/planPresets'
import { supabase } from '@/lib/supabaseClient'

// ==========================================
// DEFAULT CONFIG
// ==========================================

export const DEFAULT_CONFIG: UserConfig = {
  location: 'Hamilton',
  hourlySimulation: false,
  pvSize: 11,
  pvGeneration: 100,
  systemLosses: 10.0,
  useSolar: true,
  consumption: 1200,
  consMode: 'monthly',
  consPeakFrac: 0.25,
  consShoulderFrac: 0.1,
  consNightFrac: 0.65,
  solarPeakFrac: 0.2,
  solarShoulderFrac: 0.8,
  lineLossFactor: 11.0,
  exportLimit: 10,
  exportCurtailment: 0,
  exportPeakFrac: 0.15,
  exportShoulderFrac: 0.0,
  exportOffPeakFrac: 0.85,
  useEss: true,
  batteryCapacity: 10,
  useEv: false,
  evDistance: 40,
  evDistMode: 'daily',
  evEfficiency: 18,
  evChargeProfile: 'night',
  evBatteryCapacity: 60,
  evUpfrontCost: 45000,
  includeEvInRoi: false,
  petrolEfficiency: 8.0,
  petrolPrice: 2.80,
  evRucRate: 76.0,
  evTireWear: 200,
  evInsurancePremium: 300,
  evServicing: 150,
  evDepreciation: 2500,
  showRoi: false,
  systemCost: 22000,
  priceHike: 6.0,
  useCompounding: true,
  deletedPresetIds: [],
}

// ==========================================
// SAVED CONFIG TYPE
// ==========================================

export interface SavedConfigEntry {
  id: string
  name: string
  config: UserConfig
  isShared?: boolean
  householdId?: string | null
}

export interface Household {
  id: string
  name: string
  owner_id: string
  invite_code: string | null
  role?: 'owner' | 'admin' | 'member'
}

// ==========================================
// STORE STATE INTERFACE
// ==========================================

export interface PlanPortfolio {
  id: string
  name: string
  planIds: string[]
}

interface EnergyState {
  // Auth
  userId: string | null
  userEmail: string | null
  isAuthLoading: boolean
  isAuthenticated: boolean
  subscriptionTier: 'free' | 'pro' | 'family'

  // Plans
  plans: PlanRates[]
  setPlans: (plans: PlanRates[]) => void
  addPlan: (plan: PlanRates) => void
  updatePlan: (index: number, plan: PlanRates) => void
  removePlan: (id: string) => void
  // Portfolios
  portfolios: PlanPortfolio[]
  activePortfolioId: string | 'all'
  setActivePortfolioId: (id: string | 'all') => void
  createPortfolio: (name: string, planIds: string[]) => void
  updatePortfolio: (id: string, name: string, planIds: string[]) => void
  deletePortfolio: (id: string) => void

  // Config
  config: UserConfig
  setConfig: (config: Partial<UserConfig>) => void
  resetConfig: () => void
  savedConfigs: SavedConfigEntry[]
  activeConfigId: string
  setActiveConfigId: (id: string) => void
  saveConfigAs: (name: string) => void
  updateSavedConfig: (id: string) => void
  deleteSavedConfig: (id: string) => void
  loadSavedConfig: (id: string) => void
  renameSavedConfig: (id: string, name: string) => void

  // UI
  darkMode: boolean
  toggleDarkMode: () => void
  sidebarOpen: boolean
  toggleSidebar: () => void
  selectedPlan: string
  setSelectedPlan: (plan: string) => void
  selectedConfig: ConfigId
  setSelectedConfig: (config: ConfigId) => void
  isHelpModalOpen: boolean
  setHelpModalOpen: (open: boolean) => void
  helpSearchQuery: string
  helpTargetItem: string
  openHelpModalWithQuery: (query: string) => void
  openHelpToItem: (itemLabel: string) => void
  isPlanModalOpen: boolean
  editingPlanId: string | null
  setPlanModalOpen: (open: boolean, planId?: string | null) => void
  isPortfolioModalOpen: boolean
  setPortfolioModalOpen: (open: boolean) => void
  isHouseholdModalOpen: boolean
  setHouseholdModalOpen: (open: boolean) => void
  isPricingModalOpen: boolean
  setPricingModalOpen: (open: boolean) => void
  isDailyFlowsModalOpen: boolean
  setDailyFlowsModalOpen: (open: boolean) => void
  activeKpiModal: string | null
  setKpiModalOpen: (kpiLabel: string | null) => void
  isAuthModalOpen: boolean
  setAuthModalOpen: (open: boolean) => void
  authRecoveryMode: boolean
  userLevel: 'basic' | 'standard' | 'advanced'
  setUserLevel: (level: 'basic' | 'standard' | 'advanced') => void
  isSettingsModalOpen: boolean
  setSettingsModalOpen: (open: boolean) => void

  // Confirm Modal
  confirmState: {
    isOpen: boolean
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    isDestructive?: boolean
    type?: 'success' | 'warning' | 'error' | 'info'
    hideCancel?: boolean
    onConfirm?: (() => void) | null
  }
  openConfirm: (options: {
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    isDestructive?: boolean
    type?: 'success' | 'warning' | 'error' | 'info'
    hideCancel?: boolean
    onConfirm?: () => void
  }) => void
  closeConfirm: () => void

  // Sync
  isOnline: boolean
  setIsOnline: (online: boolean) => void
  pendingSync: boolean
  setPendingSync: (pending: boolean) => void
  lastSyncedAt: string | null
  setLastSyncedAt: (date: string) => void
  dirtyPlanIds: Set<string>
  markPlanDirty: (id: string) => void
  clearDirtyPlans: () => void
  dirtyConfigIds: Set<string>
  markConfigDirty: (id: string) => void
  clearDirtyConfigs: () => void

  // Households
  households: Household[]
  setHouseholds: (households: Household[]) => void
  activeHouseholdId: string | null
  setActiveHouseholdId: (id: string | null) => void

  // Data Management
  exportData: () => void
  importData: (data: any) => void

  // Data migration
  dataVersion: string
}

// ==========================================
// STORE IMPLEMENTATION
// ==========================================

export const useEnergyStore = create<EnergyState>()(
  persist(
    (set) => ({
      // -- Auth --
      userId: null,
      userEmail: null,
      isAuthLoading: false,
      isAuthenticated: false,
      subscriptionTier: 'free',

      // -- Plans --
      plans: BASE_MATRIX_DATA,
      setPlans: (plans) => set({ plans }),
      addPlan: (plan) =>
        set((state) => {
          const newPlan = { ...plan, id: plan.id || crypto.randomUUID() }
          return {
            plans: [...state.plans, newPlan],
            pendingSync: state.isAuthenticated,
            dirtyPlanIds: new Set([...state.dirtyPlanIds, newPlan.id])
          }
        }),
      updatePlan: (index, plan) =>
        set((state) => {
          let plans = [...state.plans]
          let id = plan.id
          const isSystemPreset = id && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
          
          let portfolios = state.portfolios
          let config = state.config

          if (isSystemPreset) {
            const newId = crypto.randomUUID()
            
            // 1. Update Portfolios to replace old ID with new UUID
            portfolios = portfolios.map(p => {
              if (p.planIds.includes(id!)) {
                return { ...p, planIds: p.planIds.map(pid => pid === id ? newId : pid) }
              }
              return p
            })
            
            // 2. Add to deletedPresetIds
            config = { ...config, deletedPresetIds: [...(config.deletedPresetIds || []), id!] }
            id = newId
          } else {
            id = id || crypto.randomUUID()
          }

          const updatedPlan = { ...plan, id }
          plans[index] = updatedPlan
          
          const selectedPlan = state.selectedPlan === plan.id ? id : state.selectedPlan

          return {
            plans,
            portfolios,
            config,
            selectedPlan,
            pendingSync: state.isAuthenticated ? true : state.pendingSync,
            dirtyPlanIds: new Set([...state.dirtyPlanIds, id])
          }
        }),
      removePlan: (planId) =>
        set((state) => {
          const planToRemove = state.plans.find(p => p.id === planId)
          if (!planToRemove) return state
          
          const isSystemPreset = !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(planId)
          
          return {
            plans: state.plans.filter((p) => p.id !== planId),
            pendingSync: state.isAuthenticated ? true : state.pendingSync,
            dirtyPlanIds: new Set([...state.dirtyPlanIds, `delete:${planId}`]),
            config: isSystemPreset 
              ? { ...state.config, deletedPresetIds: [...(state.config.deletedPresetIds || []), planId] }
              : state.config
          }
        }),

      // -- Portfolios --
      portfolios: [],
      activePortfolioId: 'all',
      setActivePortfolioId: (id) =>
        set((state) => ({
          activePortfolioId: id,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      createPortfolio: (name, planIds) =>
        set((state) => ({
          portfolios: [...state.portfolios, { id: crypto.randomUUID(), name, planIds }],
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      updatePortfolio: (id, name, planIds) =>
        set((state) => ({
          portfolios: state.portfolios.map((p) => (p.id === id ? { ...p, name, planIds } : p)),
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      deletePortfolio: (id) =>
        set((state) => ({
          portfolios: state.portfolios.filter((p) => p.id !== id),
          activePortfolioId: state.activePortfolioId === id ? 'all' : state.activePortfolioId,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),

      // -- Config --
      config: DEFAULT_CONFIG,
      setConfig: (partial) =>
        set((state) => ({
          config: { ...state.config, ...partial },
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      resetConfig: () =>
        set((state) => ({
          config: DEFAULT_CONFIG,
          activeConfigId: 'custom',
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      savedConfigs: [],
      activeConfigId: 'custom',
      setActiveConfigId: (id) =>
        set((state) => ({
          activeConfigId: id,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
       saveConfigAs: (name) =>
        set((state) => {
          const newEntry: SavedConfigEntry = {
            id: crypto.randomUUID(),
            name,
            config: { ...state.config },
          }
          return {
            savedConfigs: [...state.savedConfigs, newEntry],
            activeConfigId: newEntry.id,
            pendingSync: state.isAuthenticated ? true : state.pendingSync,
            dirtyConfigIds: new Set([...state.dirtyConfigIds, newEntry.id])
          }
        }),
      updateSavedConfig: (id) =>
        set((state) => ({
          savedConfigs: state.savedConfigs.map((c) =>
            c.id === id ? { ...c, config: { ...state.config } } : c
          ),
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
          dirtyConfigIds: new Set([...state.dirtyConfigIds, id])
        })),
      deleteSavedConfig: (id) =>
        set((state) => ({
          savedConfigs: state.savedConfigs.filter((c) => c.id !== id),
          activeConfigId: state.activeConfigId === id ? 'custom' : state.activeConfigId,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
          dirtyConfigIds: new Set([...state.dirtyConfigIds, `delete:${id}`])
        })),
      loadSavedConfig: (id) =>
        set((state) => {
          const found = state.savedConfigs.find((c) => c.id === id)
          if (!found) return {}
          return {
            config: { ...found.config },
            activeConfigId: id,
            pendingSync: state.isAuthenticated ? true : state.pendingSync,
          }
        }),
      renameSavedConfig: (id, name) =>
        set((state) => ({
          savedConfigs: state.savedConfigs.map((c) =>
            c.id === id ? { ...c, name } : c
          ),
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
          dirtyConfigIds: new Set([...state.dirtyConfigIds, id])
        })),
      // -- UI --
      confirmState: {
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        hideCancel: false,
        onConfirm: null,
      },
      openConfirm: (options) => set({ confirmState: { type: 'info', hideCancel: false, ...options, isOpen: true } }),
      closeConfirm: () => set((state) => ({ confirmState: { ...state.confirmState, isOpen: false, onConfirm: null } })),
      darkMode: false,
      toggleDarkMode: () =>
        set((state) => {
          const next = !state.darkMode
          document.documentElement.classList.toggle('dark', next)
          return {
            darkMode: next,
            pendingSync: state.isAuthenticated ? true : state.pendingSync,
          }
        }),
      sidebarOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      selectedPlan: BASE_MATRIX_DATA[0]!.plan,
      setSelectedPlan: (selectedPlan) =>
        set((state) => ({
          selectedPlan,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      selectedConfig: 'solar_bat',
      setSelectedConfig: (selectedConfig) =>
        set((state) => ({
          selectedConfig,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      isHelpModalOpen: false,
      setHelpModalOpen: (isHelpModalOpen) => set((state) => ({ isHelpModalOpen, helpSearchQuery: isHelpModalOpen ? state.helpSearchQuery : '', helpTargetItem: isHelpModalOpen ? state.helpTargetItem : '' })),
      helpSearchQuery: '',
      helpTargetItem: '',
      openHelpModalWithQuery: (query) => set({ isHelpModalOpen: true, helpSearchQuery: query, helpTargetItem: '' }),
      openHelpToItem: (itemLabel) => set({ isHelpModalOpen: true, helpTargetItem: itemLabel, helpSearchQuery: '' }),
      isPlanModalOpen: false,
      editingPlanId: null,
      setPlanModalOpen: (isPlanModalOpen, editingPlanId = null) => set({ isPlanModalOpen, editingPlanId }),
      isPortfolioModalOpen: false,
      setPortfolioModalOpen: (isPortfolioModalOpen) => set({ isPortfolioModalOpen }),
      isHouseholdModalOpen: false,
      setHouseholdModalOpen: (isHouseholdModalOpen) => set({ isHouseholdModalOpen }),
      isPricingModalOpen: false,
      setPricingModalOpen: (isPricingModalOpen) => set({ isPricingModalOpen }),
      isDailyFlowsModalOpen: false,
      setDailyFlowsModalOpen: (isDailyFlowsModalOpen) => set({ isDailyFlowsModalOpen }),
      activeKpiModal: null,
      setKpiModalOpen: (activeKpiModal) => set({ activeKpiModal }),
      isAuthModalOpen: false,
      setAuthModalOpen: (isAuthModalOpen) => set({ isAuthModalOpen, authRecoveryMode: false }),
      authRecoveryMode: false,
      userLevel: 'standard',
      setUserLevel: (userLevel) =>
        set((state) => ({
          userLevel,
          pendingSync: state.isAuthenticated ? true : state.pendingSync,
        })),
      isSettingsModalOpen: false,
      setSettingsModalOpen: (isSettingsModalOpen) => set({ isSettingsModalOpen }),

      // -- Sync --
      isOnline: navigator.onLine,
      setIsOnline: (isOnline) => set({ isOnline }),
      pendingSync: false,
      setPendingSync: (pendingSync) => set({ pendingSync }),
      lastSyncedAt: null,
      setLastSyncedAt: (lastSyncedAt) => set({ lastSyncedAt }),
      dirtyPlanIds: new Set(),
      markPlanDirty: (id) => set((s) => ({ dirtyPlanIds: new Set([...s.dirtyPlanIds, id]) })),
      clearDirtyPlans: () => set({ dirtyPlanIds: new Set() }),
      dirtyConfigIds: new Set(),
      markConfigDirty: (id) => set((s) => ({ dirtyConfigIds: new Set([...s.dirtyConfigIds, id]) })),
      clearDirtyConfigs: () => set({ dirtyConfigIds: new Set() }),

      // -- Households --
      households: [],
      setHouseholds: (households) => set({ households }),
      activeHouseholdId: null,
      setActiveHouseholdId: (activeHouseholdId) => set({ activeHouseholdId }),

      // -- Data Management --
      exportData: () => {
        const state = useEnergyStore.getState()
        const data = {
          plans: state.plans,
          config: state.config,
          savedConfigs: state.savedConfigs,
          portfolios: state.portfolios,
          activePortfolioId: state.activePortfolioId,
          activeConfigId: state.activeConfigId,
          selectedPlan: state.selectedPlan,
          selectedConfig: state.selectedConfig,
          userLevel: state.userLevel,
          version: state.dataVersion,
          exportedAt: new Date().toISOString()
        }
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `energy-scenario-backup-${new Date().toISOString().replace('T', '_').replace(/\..+/, '').replace(/:/g, '-')}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      importData: (data: any) => {
        const currentState = useEnergyStore.getState()

        // Normalize legacy data if needed
        // Support both new 'plans' and legacy 'energy_app_plans'
        let plans = data.plans || data.state?.plans || data.energy_app_plans || []
        let config = data.config || data.state?.config || data.energy_app_config || currentState.config
        let savedConfigs = data.savedConfigs || data.state?.savedConfigs || data.energy_app_saved_configs || []
        let activeConfigId = data.activeConfigId || data.state?.activeConfigId || data.energy_app_active_config_id || currentState.activeConfigId
        let portfolios = data.portfolios || data.state?.portfolios || []
        let activePortfolioId = data.activePortfolioId || data.state?.activePortfolioId || null
        let userLevel = data.userLevel || data.state?.userLevel || currentState.userLevel
        let selectedPlan = data.selectedPlan || data.state?.selectedPlan || currentState.selectedPlan
        let selectedConfig = data.selectedConfig || data.state?.selectedConfig || currentState.selectedConfig

        const isUuid = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
        const presetIds = BASE_MATRIX_DATA.map(p => p.id)

        // Map to track plan ID migrations: oldId -> newId
        const planIdMap: Record<string, string> = {}

        // 1. Migrate plan IDs to UUIDs if they are custom and not UUIDs
        plans = plans.map((p: any) => {
          let id = p.id
          if (!id) {
            id = crypto.randomUUID()
          } else if (!isUuid(id) && !presetIds.includes(id)) {
            const newId = crypto.randomUUID()
            planIdMap[id] = newId
            id = newId
          }

          const updated = {
            ...p,
            id,
          }
          if (p.freeWindow && !p.freeWindows) {
            updated.freeWindows = [p.freeWindow]
          }
          if (!updated.freeWindows) updated.freeWindows = []
          return updated
        })

        // 2. Migrate portfolio plan references using the map
        portfolios = portfolios.map((port: any) => ({
          ...port,
          planIds: (port.planIds || []).map((pid: string) => planIdMap[pid] || pid)
        }))

        // Update active portfolio and selected plan if migrated
        if (activePortfolioId && planIdMap[activePortfolioId]) {
          activePortfolioId = planIdMap[activePortfolioId]
        }
        if (selectedPlan && planIdMap[selectedPlan]) {
          selectedPlan = planIdMap[selectedPlan]
        }

        // 3. Migrate savedConfig IDs to UUIDs if not already UUIDs
        const configIdMap: Record<string, string> = {}
        savedConfigs = savedConfigs.map((c: any) => {
          let id = c.id
          if (!id) {
            id = crypto.randomUUID()
          } else if (!isUuid(id)) {
            const newId = crypto.randomUUID()
            configIdMap[id] = newId
            id = newId
          }
          return {
            ...c,
            id,
            config: { ...DEFAULT_CONFIG, ...c.config }
          }
        })

        // Update active config ID if migrated
        if (activeConfigId && configIdMap[activeConfigId]) {
          activeConfigId = configIdMap[activeConfigId]
        }

        // Apply migrations to config
        config = { ...DEFAULT_CONFIG, ...config }

        // Write to local state
        set({
          plans,
          config,
          savedConfigs,
          activeConfigId,
          portfolios,
          activePortfolioId,
          userLevel,
          selectedPlan,
          selectedConfig,
          pendingSync: false,
          dirtyPlanIds: new Set(),
          dirtyConfigIds: new Set(),
        })

        // Save to separate localStorage key so it survives logout state reset
        try {
          localStorage.setItem('energy-app-restore', JSON.stringify({
            plans, config, savedConfigs, portfolios, activePortfolioId,
            userLevel, selectedPlan, selectedConfig, activeConfigId
          }))
        } catch { /* localStorage full — ignore */ }

        // If authenticated, push restored data to Supabase immediately
        // (bypasses the debounced sync to prevent data loss on logout)
        if (currentState.isAuthenticated && currentState.userId) {
          const userId = currentState.userId

          const uuidPlanRows = plans
            .filter((p: any) => isUuid(p.id))
            .map((p: any) => ({
              id: p.id,
              user_id: userId,
              name: p.plan,
              data: p,
              is_shared: p.isShared || false,
              household_id: p.householdId || null,
              updated_at: new Date().toISOString(),
            }))

          const uuidConfigRows = savedConfigs
            .filter((c: any) => isUuid(c.id))
            .map((c: any) => ({
              id: c.id,
              user_id: userId,
              name: c.name,
              config: c.config,
              is_shared: c.isShared || false,
              household_id: c.householdId || null,
            }))

          const prefs = {
            config,
            darkMode: currentState.darkMode,
            portfolios,
            userLevel,
            activePortfolioId,
            activeConfigId,
            selectedPlan,
            selectedConfig,
            lastSyncedAt: new Date().toISOString(),
          }

          ;(async () => {
            try {
              if (uuidPlanRows.length > 0) {
                await supabase.from('plans').upsert(uuidPlanRows, { onConflict: 'id' })
              }
              if (uuidConfigRows.length > 0) {
                await supabase.from('saved_configs').upsert(uuidConfigRows, { onConflict: 'id' })
              }
              await supabase.from('user_profiles').update({ preferences: prefs }).eq('id', userId)
              set({ lastSyncedAt: new Date().toISOString() })
            } catch (err) {
              console.error('Restore sync to cloud failed:', err)
              // Data is safe in localStorage — fetchFromCloud will re-push on next login
            }
          })()
        }
      },

      // -- Data version for migrations --
      dataVersion: '4.0',
    }),
    {
      name: 'energy-app-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        plans: state.plans,
        portfolios: state.portfolios,
        activePortfolioId: state.activePortfolioId,
        config: state.config,
        savedConfigs: state.savedConfigs,
        activeConfigId: state.activeConfigId,
        darkMode: state.darkMode,
        selectedPlan: state.selectedPlan,
        selectedConfig: state.selectedConfig,
        dataVersion: state.dataVersion,
        userId: state.userId,
        userEmail: state.userEmail,
        isAuthenticated: state.isAuthenticated,
        subscriptionTier: state.subscriptionTier,
        lastSyncedAt: state.lastSyncedAt,
        households: state.households,
        activeHouseholdId: state.activeHouseholdId,
        userLevel: state.userLevel,
      }),
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version < 1) {
          // 1. Ensure all plans have IDs and migrate legacy freeWindow
          if (persistedState.plans) {
            persistedState.plans = persistedState.plans.map((p: any) => {
              const updated = {
                ...p,
                id: p.id || (p.plan ? p.plan.toLowerCase().replace(/\s+/g, '-') : crypto.randomUUID()),
              }
              // Handle legacy single window
              if (p.freeWindow && !p.freeWindows) {
                updated.freeWindows = [p.freeWindow]
                delete updated.freeWindow
              }
              if (!updated.freeWindows) updated.freeWindows = []
              return updated
            })
          }

          // 2. Merge default config into current config
          if (persistedState.config) {
            persistedState.config = { ...DEFAULT_CONFIG, ...persistedState.config }
          }

          // 3. Update saved configs
          if (persistedState.savedConfigs) {
            persistedState.savedConfigs = persistedState.savedConfigs.map((c: any) => ({
              ...c,
              config: { ...DEFAULT_CONFIG, ...c.config }
            }))
          }

          return persistedState
        }
        return persistedState
      },
    }
  )
)

// ==========================================
// SELECTOR HOOKS (for performance)
// ==========================================

export const usePlans = () => useEnergyStore((s) => s.plans)
export const usePortfolios = () => useEnergyStore((s) => s.portfolios)
export const useActivePortfolioId = () => useEnergyStore((s) => s.activePortfolioId)
export const useConfig = () => useEnergyStore((s) => s.config)
export const useDarkMode = () => useEnergyStore((s) => s.darkMode)
export const useSelectedPlan = () => useEnergyStore((s) => s.selectedPlan)
export const useSelectedConfig = () => useEnergyStore((s) => s.selectedConfig)
export const useAuthState = () =>
  useEnergyStore((s) => ({
    userId: s.userId,
    userEmail: s.userEmail,
    isAuthLoading: s.isAuthLoading,
    isAuthenticated: s.isAuthenticated,
  }))
export const useSyncState = () =>
  useEnergyStore((s) => ({
    isOnline: s.isOnline,
    pendingSync: s.pendingSync,
    lastSyncedAt: s.lastSyncedAt,
  }))
